import React, { useMemo } from 'react';

import { MediaIcon } from '@change/ds-icons';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { MediaSource } from '#src/app/pages/petitionDetails/shared/types';

const MAX_VISIBLE_LOGO_COUNT = 3;

const LOGO_BUTTON_CLASS =
	'flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full border-0 bg-[#E1DDE9] p-0 text-[#403A49] transition-transform duration-150 ease-out hover:-translate-y-1';

export type MediaOutletLogosProps = {
	mediaSources: readonly MediaSource[];
	onLogoClick: () => void;
	testIdPrefix?: string;
};

export const MediaOutletLogos: React.FC<MediaOutletLogosProps> = ({
	mediaSources,
	onLogoClick,
	testIdPrefix = 'momentum-bar',
}) => {
	const { environment } = useUtilityContext();
	const visibleSources = mediaSources.slice(0, MAX_VISIBLE_LOGO_COUNT);

	const imageHost = useMemo(() => {
		const env = environment.getEnvironment();
		switch (env) {
			case 'production':
				return 'https://assets.change.org/';
			case 'demo':
				return 'https://assets.demo.changeeng.org/';
			case 'staging':
			default:
				return 'https://assets.staging-change.org/';
		}
	}, [environment]);

	if (visibleSources.length === 0) {
		return null;
	}

	return (
		<div className="flex flex-row flex-wrap items-center gap-1" data-testid={`${testIdPrefix}-media-outlet-logos`}>
			{visibleSources.map((hit, index) => (
				<MediaOutletLogo
					key={hit.commentId}
					index={index}
					testIdPrefix={testIdPrefix}
					src={hit.fullSizePath}
					imageHost={imageHost}
					onLogoClick={onLogoClick}
				/>
			))}
		</div>
	);
};

type MediaOutletLogoProps = {
	index: number;
	testIdPrefix: string;
	src?: string | null;
	imageHost: string;
	onLogoClick: () => void;
};

function MediaOutletLogo({ index, testIdPrefix, src, imageHost, onLogoClick }: MediaOutletLogoProps) {
	const { translate } = useI18n();
	const label = translate('corgi.petition_details.momentum_bar.media_outlet_title');

	return (
		<button
			type="button"
			className={LOGO_BUTTON_CLASS}
			data-testid={`${testIdPrefix}-media-outlet-logo-${index}`}
			aria-label={label}
			onClick={onLogoClick}
		>
			{src ? (
				<div>
					<img src={`${imageHost}${src}`} alt="" />
				</div>
			) : (
				<MediaIcon size="small" aria-hidden />
			)}
		</button>
	);
}
